import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { goalInboxID } from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

import { filterEmptyChildrenCascading } from './use-life-area-tree';

export type Options = {
  goals: { id: ID; name: string }[];
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
  hideNoGoalGroup?: boolean;
};

export const useGoalTree = ({
  goals,
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
  hideNoGoalGroup,
}: Options) => {
  const { t } = useTranslation();

  return useMemo<Record<ID, TreeItem>>(() => {
    const goalTreeItems = goals.reduce<Record<ID, TreeItem>>((acc, goal) => {
      acc[goal.id] = {
        id: goal.id,
        type: TreeItemTypes.Goal,
        name: goal.name,
        children: [],
        isFolder: true,
      };
      return acc;
    }, {});

    if (!hideNoGoalGroup) {
      goalTreeItems[goalInboxID] = {
        id: goalInboxID,
        type: TreeItemTypes.Goal,
        name: t('general.sections.goals.other.title'),
        children: [],
        isFolder: true,
      };
    }

    const customTaskOrder =
      sortingMode === SortingMode.Custom
        ? Object.keys(goalTreeItems).reduce<ID[]>((acc, goalId) => {
            const order = customOrder?.[goalId] ?? [];
            acc.push(...order);
            return acc;
          }, [])
        : [];

    // process tasks to tree items to finish it off with
    const allTreeItems = getSortedTasks(tasks, {
      today,
      weekStartsOn,
      order: customTaskOrder,
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    }).reduce<Record<ID, TreeItem>>((acc, task) => {
      const parentId = task.goalId ?? goalInboxID;
      const parent = acc[parentId];
      if (parent) {
        parent.children?.push(task.id);
        acc[task.id] = {
          id: task.id,
          name: task.name,
          type: TreeItemTypes.Task,
        };
      }
      return acc;
    }, goalTreeItems);

    // Add New Task row to each Goal section
    Object.keys(goalTreeItems).forEach((goalId) => {
      const parent = allTreeItems[goalId];
      if (parent) {
        if (!parent.children?.length && !showEmptyGroups) {
          return;
        }

        const newTaskItemId = `${goalId}-new-task`;
        parent.children?.push(newTaskItemId);
        allTreeItems[newTaskItemId] = {
          id: newTaskItemId,
          name: 'New Task',
          type: TreeItemTypes.NewTask,
        };
      }
    });

    const filtered = filterEmptyChildrenCascading(allTreeItems);
    const topLevelIds = Object.keys(filtered).filter(
      (id) => filtered[id].type === TreeItemTypes.Goal,
    );

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: topLevelIds,
      },
      ...filtered,
    };
  }, [
    customOrder,
    goals,
    sortingMode,
    t,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
    hideNoGoalGroup,
  ]);
};
